import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Box,
  Stack,
  Chip,
  Skeleton,
  Modal,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

import Header1 from "../Header/Header1";
import Footer2 from "../Footer/Footer2";
import { useAuth } from "../../AuthContext";
import AskNavBar from "./AskNavBar";
import Platform from "../Platform/Platform";

const API_URL = process.env.REACT_APP_API_URL;

const MyRequestsListExpert = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  // Infinite scroll
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const [hasMore, setHasMore] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const observer = useRef();

  // Modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);

  const openRequestModal = (req) => {
    setSelectedReq(req);
    setOpenModal(true);
  };

  const closeRequestModal = () => setOpenModal(false);

  // --- FETCH FUNCTION ---
  const fetchRequests = async () => {
    if (!userId || loading || !hasMore) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/get-user-requests-expert/`, {
        user_id: userId,
        page,
        page_size: pageSize,
        search: search.trim(),
        startDate,
        endDate,
      });

      const newResults = response.data.results || [];

      // Remove duplicates based on ID
      setRequests((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const filteredNew = newResults.filter((r) => !existingIds.has(r.id));
        return [...prev, ...filteredNew];
      });

      setHasMore(page < response.data.total_pages);
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
    setLoading(false);
  };

  // --- EFFECTS ---
  useEffect(() => {
    fetchRequests();
  }, [page]);

  useEffect(() => {
    // Reset when filters change
    setRequests([]);
    setPage(1);
    setHasMore(true);
    fetchRequests();
  }, [search, startDate, endDate]);

  // --- INFINITE SCROLL ---
  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // --- ACCEPT / REJECT HANDLER ---
  const handleRequestAction = async (requestId, action) => {
    try {
      const response = await axios.post(`${API_URL}/api/respond-request/`, {
        user_id: userId,
        request_id: requestId,
        action,
      });

      if (response.data.success) {
        if (action === "accept") {
          setRequests((prev) =>
            prev.map((r) =>
              r.id === requestId ? { ...r, status: "accepted", experts: [userId] } : r
            )
          );
        } else if (action === "reject") {
          setRequests((prev) =>
            prev.map((r) =>
              r.id === requestId
                ? { ...r, experts: r.experts.filter((uid) => uid !== userId) }
                : r
            )
          );
        }

        // Close modal after action
        setOpenModal(false);
      }
    } catch (error) {
      console.error("Error updating request:", error);
    }
  };

  const handleJoinMeeting = (meetingLink) => {
  if (!meetingLink) return;
  window.open(meetingLink, "_blank", "noopener,noreferrer");
};

  return (
    <div>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1300,
        }}
      >
        <Header1 />
      </Box>

      <Platform />
      <AskNavBar />

      {/* ANALYTICS BUTTON */}
      {/* <Box sx={{ textAlign: "center", mt: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate("/my-requests-analytics")}
        >
          Show Analytics
        </Button>
      </Box> */}

      <Box sx={{ p: 3, ml: 5, minHeight: { xs: '40vh', sm: '50vh', md: '60vh' }, }}>
        {/* FILTERS */}
<Box
  sx={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    mb: 3,
  }}
>
  {/* Search */}
  <Box
    sx={{
      width: {
        xs: '100%',
        md: '33.3333%',
      },
    }}
  >
    <TextField
      label="Search"
      fullWidth
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />
  </Box>

  {/* Start Date */}
  <Box
    sx={{
      width: {
        xs: '100%',
        md: '33.3333%',
      },
    }}
  >
    <TextField
      label="Start Date"
      type="date"
      fullWidth
      InputLabelProps={{ shrink: true }}
      value={startDate}
      onChange={(e) => setStartDate(e.target.value)}
    />
  </Box>

  {/* End Date */}
  <Box
    sx={{
      width: {
        xs: '100%',
        md: '33.3333%',
      },
    }}
  >
    <TextField
      label="End Date"
      type="date"
      fullWidth
      InputLabelProps={{ shrink: true }}
      value={endDate}
      onChange={(e) => setEndDate(e.target.value)}
    />
  </Box>
</Box>

        {/* REQUEST LIST */}
<Box
  sx={{
    display: "flex",
    flexWrap: "wrap",
    gap: 3,
  }}
>
  {requests.map((req, index) => {
    const isLast = requests.length === index + 1;

    return (
      <Box
        key={req.id}
        ref={isLast ? lastElementRef : null}
        sx={{
          width: {
            xs: "100%",
            sm: "calc(50% - 12px)",
            md: "calc(33.333% - 16px)",
          },
          display: "flex",
        }}
      >
        <Card
          sx={{
            borderRadius: 4,
            background:
              "linear-gradient(145deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
            backdropFilter: "blur(10px)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            p: 2,
            transition: "all 0.35s ease",
            "&:hover": {
              boxShadow: "0 20px 45px rgba(0,0,0,0.6)",
              transform: "translateY(-6px) scale(1.01)",
            },
            cursor: "pointer",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "100%",
          }}
          onClick={() => openRequestModal(req)}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Description */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                lineHeight: 1.4,
                mb: 1,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {req.description}
            </Typography>

            {/* Date & Time */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                fontSize: 14,
                opacity: 0.85,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography>
                📅{" "}
                {req.selectedDate
                  ? new Date(req.selectedDate).toLocaleDateString("en-GB")
                  : "-"}
              </Typography>

              <Typography>
                ⏰{" "}
                {req.startTime
                  ? (() => {
                      const [hour, minute] = req.startTime.split(":");
                      const h = parseInt(hour, 10);
                      const ampm = h >= 12 ? "PM" : "AM";
                      const hour12 = h % 12 === 0 ? 12 : h % 12;
                      return `${hour12}:${minute} ${ampm}`;
                    })()
                  : "-"}
              </Typography>
            </Box>

            {/* Info Row */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                mb: 1,
              }}
            >
              <Chip
                label={`₹ ${req.price}`}
                sx={{
                  background: "linear-gradient(135deg, #7F5AF0, #2CB67D)",
                  color: "#fff",
                  fontWeight: 600,
                  minWidth: 80,
                }}
              />
              <Chip
                label={`${req.duration} Mins`}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  minWidth: 80,
                }}
              />
              <Chip
                label={req.language || "-"}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  minWidth: 80,
                }}
              />
            </Box>

            {/* Status */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Chip
                label={req.status}
                sx={{
                  textTransform: "capitalize",
                  backgroundColor:
                    req.status === "confirmed"
                      ? "rgba(44,182,125,0.2)"
                      : req.status === "accepted"
                      ? "rgba(127,90,240,0.2)"
                      : "rgba(255,255,255,0.12)",
                  color:
                    req.status === "confirmed"
                      ? "#2CB67D"
                      : req.status === "accepted"
                      ? "#7F5AF0"
                      : "#fff",
                  fontWeight: 500,
                }}
              />
            </Box>

            {/* Created & Updated */}
            <Box
              sx={{
                mt: 1,
                fontSize: 12,
                opacity: 0.6,
                display: "flex",
                flexDirection: "column",
                gap: 0.3,
              }}
            >
              {req.created_at && (
                <Typography>
                  🟢 Created:{" "}
                  {new Date(req.created_at).toLocaleString("en-GB", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </Typography>
              )}
              {req.updated_at && (
                <Typography>
                  🔵 Last Updated:{" "}
                  {new Date(req.updated_at).toLocaleString("en-GB", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  })}

  {/* Skeleton Loader */}
  {loading &&
    [...Array(3)].map((_, i) => (
      <Box
        key={i}
        sx={{
          width: {
            xs: "100%",
            sm: "calc(50% - 12px)",
            md: "calc(33.333% - 16px)",
          },
        }}
      >
        <Card sx={{ p: 2 }}>
          <Skeleton height={150} variant="rectangular" />
          <Skeleton width="60%" sx={{ mt: 2 }} />
          <Skeleton width="40%" />
        </Card>
      </Box>
    ))}
</Box>

        {!hasMore && requests.length > 0 && (
          <Typography textAlign="center" color="gray" sx={{ mt: 4 }}>
            No more results
          </Typography>
        )}
      </Box>

      <Footer2 />

      {/* MODAL */}
      <Modal open={openModal} onClose={closeRequestModal}>
  <Box
    sx={{
      width: "90%",
      maxWidth: 600,
      bgcolor: "#111111",
      p: 4,
      mx: "auto",
      mt: { xs: 5, sm: 10 },
      borderRadius: 3,
      boxShadow: "0 15px 40px rgba(0,0,0,0.6)",
      color: "#fff",
      position: "relative",
      display: "flex",
      flexDirection: "column",
      transition: "all 0.3s ease",
    }}
  >
    {/* Close Button */}
    <IconButton
      sx={{
        position: "absolute",
        top: 16,
        right: 16,
        color: "#fff",
        backgroundColor: "rgba(255,255,255,0.08)",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
      }}
      onClick={closeRequestModal}
    >
      <CloseIcon />
    </IconButton>

    {selectedReq && (
      <>
        {/* Description Hero */}
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            mb: 3,
            lineHeight: 1.6,
            background: "rgba(255,255,255,0.03)",
            p: 2,
            borderRadius: 2,
            fontSize: { xs: 16, sm: 18 },
          }}
        >
          {selectedReq.description || "No Description Available"}
        </Typography>

        {/* Language */}
        {selectedReq.language && (
          <Typography
            sx={{
              mb: 2,
              fontSize: 14,
              color: "rgba(255,255,255,0.7)",
            }}
          >
            <strong>Language:</strong> {selectedReq.language}
          </Typography>
        )}

        {/* Date & Time */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, fontSize: 14, opacity: 0.85 }}
        >
          <Typography>
            📅{" "}
            {selectedReq.selectedDate
              ? new Date(selectedReq.selectedDate).toLocaleDateString("en-GB")
              : "-"}
          </Typography>

          <Typography>
            ⏰{" "}
            {selectedReq.startTime
              ? (() => {
                  const [hour, minute] = selectedReq.startTime.split(":");
                  const h = parseInt(hour, 10);
                  const ampm = h >= 12 ? "PM" : "AM";
                  const hour12 = h % 12 === 0 ? 12 : h % 12;
                  return `${hour12}:${minute} ${ampm}`;
                })()
              : "-"}
          </Typography>
        </Stack>

        {/* Price & Status */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Chip
            label={`₹ ${selectedReq.price}`}
            sx={{
              background: "linear-gradient(135deg, #7F5AF0, #2CB67D)",
              color: "#fff",
              fontWeight: 600,
              minWidth: 80,
            }}
          />

          <Chip
            label={selectedReq.status}
            sx={{
              textTransform: "capitalize",
              backgroundColor:
                selectedReq.status === "confirmed"
                  ? "rgba(44,182,125,0.2)"
                  : selectedReq.status === "accepted"
                  ? "rgba(127,90,240,0.2)"
                  : "rgba(255,255,255,0.12)",
              color:
                selectedReq.status === "confirmed"
                  ? "#2CB67D"
                  : selectedReq.status === "accepted"
                  ? "#7F5AF0"
                  : "#fff",
              fontWeight: 500,
            }}
          />
        </Stack>

        {/* Media Button */}
        {selectedReq.media && (
          <Button
            variant="outlined"
            href={selectedReq.media}
            target="_blank"
            fullWidth
            sx={{
              mb: 2,
              py: 1.5,
              fontWeight: 600,
              color: "#7F5AF0",
              borderColor: "rgba(127,90,240,0.5)",
              "&:hover": {
                borderColor: "#7F5AF0",
                backgroundColor: "rgba(127,90,240,0.1)",
              },
            }}
          >
            View Media
          </Button>
        )}

        {/* Confirm / Join Buttons */}
        {selectedReq.status === "confirmed" && (
          <Button
            variant="contained"
            color="success"
            fullWidth
            sx={{ mb: 2, py: 1.5, fontWeight: 600, textTransform: "none" }}
            disabled={!selectedReq.meeting_link}
            onClick={() => handleJoinMeeting(selectedReq.meeting_link)}
          >
            {selectedReq.meeting_link ? "Join Now" : "Meeting Link Not Available"}
          </Button>
        )}

        {/* Accept / Not Interested */}
        {selectedReq.status !== "confirmed" && (
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => handleRequestAction(selectedReq.id, "accept")}
              disabled={selectedReq.status === "accepted"}
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => handleRequestAction(selectedReq.id, "reject")}
            >
              Not Interested
            </Button>
          </Stack>
        )}

        {/* Timeline */}
        <Stack
          direction="column"
          spacing={0.5}
          sx={{ mt: 3, fontSize: 12, opacity: 0.6 }}
        >
          {selectedReq.created_at && (
            <Typography>
              🟢 Created:{" "}
              {new Date(selectedReq.created_at).toLocaleString("en-GB", {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </Typography>
          )}
          {selectedReq.updated_at && (
            <Typography>
              🔵 Last Updated:{" "}
              {new Date(selectedReq.updated_at).toLocaleString("en-GB", {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </Typography>
          )}
        </Stack>
      </>
    )}
  </Box>
</Modal>

    </div>
  );
};

export default MyRequestsListExpert;
